import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  Home, 
  MapPin, 
  Phone, 
  Mail, 
  Star,
  Building2,
  Award,
  Calendar,
  MessageSquare,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RealtorProfilePage({ user, subscription, onLogout }) {
  const { realtorId } = useParams();
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [loading, setLoading] = useState(true);
  const { mainClass } = useSidebar();

  useEffect(() => {
    fetchRealtorProfile();
  }, [realtorId]);

  const fetchRealtorProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/realtor/directory/${realtorId}`, {
        withCredentials: true
      });
      setRealtor(response.data);
    } catch (error) {
      console.error("Failed to fetch realtor:", error);
      toast.error("Realtor not found");
      navigate("/realtors");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!realtor) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        <div className="p-6 lg:p-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="text-white/50 hover:text-white mb-6"
            onClick={() => navigate("/realtors")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Realtor Network
          </Button>

          {/* Profile Header */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-3xl flex-shrink-0">
                {realtor.profile_image ? (
                  <img src={realtor.profile_image} alt={realtor.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  realtor.name?.charAt(0) || "R"
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-white">{realtor.name}</h1>
                    <p className="text-white/50 flex items-center gap-2 mt-1">
                      <Building2 className="w-4 h-4" />
                      {realtor.company || "Independent Agent"}
                    </p>
                    {realtor.city && (
                      <p className="text-white/40 flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4" />
                        {realtor.city}, {realtor.state}
                      </p>
                    )}
                  </div>
                  {realtor.rating && (
                    <div className="flex items-center gap-2 bg-gold/20 px-4 py-2 rounded-2xl">
                      <Star className="w-5 h-5 text-gold fill-gold" />
                      <span className="text-gold font-bold text-lg">{realtor.rating}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 mt-4">
                  {realtor.deals_closed > 0 && (
                    <div className="flex items-center gap-2 text-white/70">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>{realtor.deals_closed} Deals Closed</span>
                    </div>
                  )}
                  {realtor.years_experience && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Calendar className="w-4 h-4 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                      <span>{realtor.years_experience} Years Experience</span>
                    </div>
                  )}
                  {realtor.license_number && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Award className="w-4 h-4 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                      <span>License: {realtor.license_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Bio */}
              {realtor.bio && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4">About</h2>
                  <p className="text-white/70 leading-relaxed">{realtor.bio}</p>
                </div>
              )}

              {/* Specialties */}
              {realtor.specialties && realtor.specialties.length > 0 && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4">Specialties</h2>
                  <div className="flex flex-wrap gap-2">
                    {realtor.specialties.map((specialty, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-white/[0.08] text-white/70 rounded-full text-sm"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Markets */}
              {realtor.markets && realtor.markets.length > 0 && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4">Markets Served</h2>
                  <div className="flex flex-wrap gap-2">
                    {realtor.markets.map((market, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        {market}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h2 className="text-lg font-bold text-white mb-4">Contact</h2>
                <div className="space-y-3">
                  {realtor.phone && (
                    <Button 
                      className="w-full justify-start bg-white/[0.08] hover:bg-white/[0.1] text-white"
                      onClick={() => window.open(`tel:${realtor.phone}`)}
                    >
                      <Phone className="w-4 h-4 mr-3" />
                      {realtor.phone}
                    </Button>
                  )}
                  {realtor.email && (
                    <Button 
                      className="w-full justify-start bg-white/[0.08] hover:bg-white/[0.1] text-white"
                      onClick={() => window.open(`mailto:${realtor.email}`)}
                    >
                      <Mail className="w-4 h-4 mr-3" />
                      {realtor.email}
                    </Button>
                  )}
                  <Button 
                    className="w-full gold-gradient-btn mt-4"
                    onClick={() => toast.info("Messaging feature coming soon!")}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
